import React, {useEffect, useMemo} from 'react';

import {format} from 'date-fns';
import {useGate, useStore} from 'effector-react';
import i18next from 'i18next';
import {useTranslation} from 'react-i18next';

import {useLocation} from 'react-router-dom';

import {validateCode} from 'api';
import {BackTo} from 'components/common/BackTo';
import {Stepper} from 'components/ui-kit/Stepper';
import {DocumentReportType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {
    createUtilisationDocument,
    DocumentsCreateGate,
    isUtilisationGroup$,
    step$,
    utilisationForm$,
    utilisationGroupChanged,
} from 'models/documents/create';
import {CodeRequestType, CodeResponseType} from 'types/validation';

import {ProductCodes} from '../common/products';
import {ContentWrapper} from '../common/styled';

import {BatchNumberModal} from './BatchNumberModal';
import {DocumentForm} from './form';
import {UtilisationDocumentGroupCodes} from './groupCodes';

export const CreateUtilisation = () => {
    const {t} = useTranslation();
    const step = useStore(step$);
    const utilisationForm = useStore(utilisationForm$);
    const isGroup = useStore(isUtilisationGroup$);
    useGate(DocumentsCreateGate);

    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const groupParam = params.get('group');
        if (groupParam !== null) {
            utilisationGroupChanged(groupParam === 'true');
        }
    }, [location.search]);

    const validation = async (
        markingCode: string,
        data: CodeResponseType['info'][],
        batchNumber: string,
    ) => {
        const errors: string[] = [];
        const DATE_PATTERN = "yyyy-MM-dd'T'HH:mm:ss.sss'Z'";
        const scannedCodes = isGroup ? data.map((d) => d.identityCode) : [];

        try {
            const payload: CodeRequestType = {
                storageLocationId: String(utilisationForm.storageLocationId),
                manufacturingDate: format(
                    new Date(utilisationForm.manufacturingDate!),
                    DATE_PATTERN,
                ),
                ...(utilisationForm.groupMarkingCode && {
                    aggregatorMarkingCode: utilisationForm.groupMarkingCode,
                }),
                applicationDate: format(new Date(utilisationForm.applicationDate!), DATE_PATTERN),
                type: DocumentReportType.UTILISATION,
                markingCode,
                batchNumber,
                scannedCodes,
            };
            const codeData = (await validateCode(payload)).data;
            errors.push(...codeData.errors.map((e) => e.message));

            if (data.find((d) => d.identityCode === codeData.info.identityCode))
                errors.push(i18next.t('components.scan.errors.double'));

            return {errors, codeData: codeData.info};
        } catch (e) {
            console.error(e);
            errors.push('Invalid code');
            return {errors, codeData: null};
        }
    };

    const steps = useMemo(() => {
        if (isGroup)
            return [
                {
                    label: t('pages.documents.utilisationSteps.stepGeneral.label'),
                    component: <DocumentForm />,
                },
                {
                    label: t('pages.documents.utilisationSteps.stepGroup.label'),
                    component: <UtilisationDocumentGroupCodes />,
                },
                {
                    label: t('pages.documents.utilisationSteps.stepUnits.label'),
                    component: (
                        <ProductCodes
                            createDocument={createUtilisationDocument}
                            validation={validation}
                            prevStep={1}
                        />
                    ),
                },
            ];

        return [
            {
                label: t('pages.documents.stepOne'),
                component: <DocumentForm />,
            },
            {
                label: t('pages.documents.stepTwo'),
                component: (
                    <ProductCodes
                        createDocument={createUtilisationDocument}
                        validation={validation}
                    />
                ),
            },
        ];
    }, [isGroup, utilisationForm]);

    return (
        <>
            <BatchNumberModal />
            <BackTo to={ROUTE.DOCUMENTS_DOCUMENTS}>{t('pages.documents.back')}</BackTo>
            <ContentWrapper>
                <h1>{t('pages.documents.newDocumentUtilisation')}</h1>
                <Stepper steps={steps} activeStep={step} />
            </ContentWrapper>
        </>
    );
};
